import type { SessionTokens } from './types';

/** Where the two clients keep their tokens. Two independent slots: customer and admin. */
export const TOKEN_STORAGE_KEYS = {
  customer: 'sf.customer.tokens',
  admin: 'sf.admin.tokens',
} as const;

export type ClientKind = keyof typeof TOKEN_STORAGE_KEYS;

export interface StoredTokens extends SessionTokens {
  /** Epoch ms at which the access token expires (computed on write from `expiresIn`). */
  expiresAt?: number;
}

export interface TokenStorage {
  read(): StoredTokens | null;
  write(tokens: StoredTokens | null): void;
}

/** localStorage-backed storage; degrades to memory when storage is unavailable (private mode, SSR). */
export function createTokenStorage(key: string): TokenStorage {
  let memory: StoredTokens | null = null;
  const ls = (): Storage | null => {
    try {
      return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
      return null;
    }
  };
  return {
    read() {
      const store = ls();
      if (!store) return memory;
      try {
        const raw = store.getItem(key);
        return raw ? (JSON.parse(raw) as StoredTokens) : null;
      } catch {
        return null;
      }
    },
    write(tokens) {
      memory = tokens;
      const store = ls();
      if (!store) return;
      try {
        if (tokens) store.setItem(key, JSON.stringify(tokens));
        else store.removeItem(key);
      } catch {
        /* quota / disabled storage: memory copy is enough for this session */
      }
    },
  };
}

/** Storage that never touches the browser — handy in tests. */
export function createMemoryTokenStorage(initial: StoredTokens | null = null): TokenStorage {
  let value = initial;
  return {
    read: () => value,
    write: (t) => {
      value = t;
    },
  };
}
